import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    payment_method: 'stripe'
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('ninacare_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate('/shop');
    }
  }, []);

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    
    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        }))
      };
      
      const response = await axios.post(`${API}/shop/checkout`, orderData);
      
      // Rediriger vers Stripe
      window.location.href = response.data.url;
      
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast.error('Erreur lors de la création de la session de paiement');
      setLoading(false);
    }
  };

  const total = getTotal();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '80px 20px' }}>
      <div className="container" style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <h1 className="hero-medium" style={{ marginBottom: '40px', textAlign: 'center' }}>
          Paiement
        </h1>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '40px'
        }}>
          {/* Formulaire */}
          <div style={{ background: 'white', padding: '32px', borderRadius: '8px' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Informations de facturation
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Nom complet *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label className="body-regular" style={{ display: 'block', marginBottom: '12px', fontWeight: 600 }}>
                  Mode de paiement
                </label>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    padding: '16px',
                    border: formData.payment_method === 'stripe' ? '2px solid #C9A961' : '1px solid var(--border-medium)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: formData.payment_method === 'stripe' ? 'rgba(201, 169, 97, 0.05)' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={formData.payment_method === 'stripe'}
                      onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                      style={{ marginRight: '12px' }}
                    />
                    <strong>Paiement en une fois</strong>
                    <p className="body-small" style={{ marginTop: '4px', marginLeft: '28px', color: 'var(--text-secondary)' }}>
                      Carte bancaire via Stripe
                    </p>
                  </label>

                  {total >= 50 && (
                    <label style={{
                      padding: '16px',
                      border: formData.payment_method === 'stripe_installments' ? '2px solid #C9A961' : '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.payment_method === 'stripe_installments' ? 'rgba(201, 169, 97, 0.05)' : 'white'
                    }}>
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe_installments"
                        checked={formData.payment_method === 'stripe_installments'}
                        onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                        style={{ marginRight: '12px' }}
                      />
                      <strong>Paiement en plusieurs fois</strong>
                      <p className="body-small" style={{ marginTop: '4px', marginLeft: '28px', color: 'var(--text-secondary)' }}>
                        Payez en 3 ou 4 fois sans frais (montant minimum: 50€)
                      </p>
                    </label>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
                style={{
                  width: '100%',
                  background: loading ? '#ccc' : '#C9A961',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Redirection...
                  </>
                ) : (
                  <>
                    <CreditCard size={20} />
                    Procéder au paiement
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Résumé de la commande */}
          <div style={{ background: 'white', padding: '32px', borderRadius: '8px', height: 'fit-content' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Résumé de la commande
            </h2>

            {cart.map(item => (
              <div
                key={item.product_id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid var(--border-light)'
                }}
              >
                <div>
                  <p className="body-regular" style={{ fontWeight: 600 }}>
                    {item.name}
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Quantité: {item.quantity}
                  </p>
                </div>
                <p className="body-regular" style={{ fontWeight: 600, color: '#C9A961' }}>
                  {(item.price * item.quantity).toFixed(2)} €
                </p>
              </div>
            ))}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '2px solid var(--border-light)'
            }}>
              <span className="heading-2">Total</span>
              <span className="heading-2" style={{ color: '#C9A961' }}>
                {total.toFixed(2)} €
              </span>
            </div>

            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(201, 169, 97, 0.1)',
              borderRadius: '8px'
            }}>
              <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                🔒 Paiement 100% sécurisé par Stripe<br/>
                💳 Cartes acceptées: Visa, Mastercard, Amex<br/>
                📧 Vous recevrez une confirmation par email
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default Checkout;
